import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Shield, Users, TrendingUp, Activity, DollarSign, Clock,
  CheckCircle2, AlertTriangle, XCircle, Ban, RotateCcw, Search,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Uniform status → badge colour map, keeps table + stats consistent.
const STATUS_META = {
  trial:     { label: 'Trial',     className: 'bg-blue-100 text-blue-800',       icon: Clock },
  active:    { label: 'Active',    className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  expired:   { label: 'Expired',   className: 'bg-amber-100 text-amber-800',     icon: AlertTriangle },
  unpaid:    { label: 'Unpaid',    className: 'bg-slate-200 text-slate-800',     icon: XCircle },
  suspended: { label: 'Suspended', className: 'bg-red-100 text-red-800',         icon: Ban },
  exempt:    { label: 'Admin',     className: 'bg-purple-100 text-purple-800',   icon: Shield },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};
const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const formatINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, className: 'bg-slate-100 text-slate-700', icon: Clock };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${meta.className}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState('subscribers');

  // Guard: only admins may load this page.
  if (user && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center">
          <Shield className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="admin-heading">Owner Dashboard</h1>
          <p className="text-sm text-slate-500">Manage subscribers, trials, offline activations, and revenue.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="subscribers" data-testid="tab-subscribers"><Users className="h-4 w-4 mr-1.5" />Subscribers</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><TrendingUp className="h-4 w-4 mr-1.5" />Audit Log</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity"><Activity className="h-4 w-4 mr-1.5" />Activity</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue"><DollarSign className="h-4 w-4 mr-1.5" />Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="subscribers" className="mt-4">
          <SubscribersPanel />
        </TabsContent>
        <TabsContent value="audit" className="mt-4">
          <AuditLogPanel />
        </TabsContent>
        <TabsContent value="activity" className="mt-4">
          <ActivityPanel />
        </TabsContent>
        <TabsContent value="revenue" className="mt-4">
          <RevenuePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ============================================================
// Panel 1 + 2 — Subscribers table with row-level actions
// ============================================================
function SubscribersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  // Modal state — one modal open at a time, driven by the row being acted on.
  const [action, setAction] = useState(null);   // 'extend' | 'activate' | 'suspend' | 'reactivate'
  const [target, setTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy, order };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${API}/api/admin/subscribers`, { params, withCredentials: true });
      setRows(data.subscribers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sortBy, order]);

  const openAction = (kind, row) => { setTarget(row); setAction(kind); };
  const closeAction = (reload = false) => {
    setAction(null); setTarget(null);
    if (reload) load();
  };

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search name, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="pl-9"
            data-testid="subscribers-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="subscribers-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="exempt">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[180px]" data-testid="subscribers-sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at">Sort · Signup date</SelectItem>
            <SelectItem value="trial_ends_at">Sort · Trial end</SelectItem>
            <SelectItem value="subscription_end">Sort · Subscription end</SelectItem>
            <SelectItem value="name">Sort · Name</SelectItem>
            <SelectItem value="email">Sort · Email</SelectItem>
            <SelectItem value="last_login">Sort · Last login</SelectItem>
          </SelectContent>
        </Select>
        <Select value={order} onValueChange={setOrder}>
          <SelectTrigger className="w-[120px]" data-testid="subscribers-order"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Descending</SelectItem>
            <SelectItem value="asc">Ascending</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} variant="outline" data-testid="subscribers-refresh">Refresh</Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Agent</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Signed up</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Trial / Sub ends</th>
              <th className="px-3 py-2 text-left">Plan</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="subscribers-loading">Loading subscribers…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="subscribers-empty">No subscribers match this filter.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`subscriber-row-${r.user_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{r.name || '—'}</div>
                  <div className="text-xs text-slate-500">{r.role}</div>
                </td>
                <td className="px-3 py-2">
                  <div>{r.email}</div>
                  <div className="text-xs text-slate-500">{r.phone || '—'}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.created_at)}</td>
                <td className="px-3 py-2"><StatusBadge status={r.effective_status} /></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.effective_status === 'active'
                    ? formatDate(r.subscription_end)
                    : r.effective_status === 'trial'
                    ? formatDate(r.trial_ends_at)
                    : '—'}
                  {r.days_remaining > 0 && (r.effective_status === 'trial' || r.effective_status === 'active') && (
                    <div className="text-xs text-slate-500">{r.days_remaining}d left</div>
                  )}
                </td>
                <td className="px-3 py-2">{r.current_plan ? STATUS_META[r.current_plan]?.label || r.current_plan : '—'}</td>
                <td className="px-3 py-2 text-right">
                  {r.role !== 'admin' && (
                    <div className="flex justify-end gap-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => openAction('extend', r)} data-testid={`action-extend-${r.user_id}`}>Extend</Button>
                      <Button size="sm" variant="outline" onClick={() => openAction('activate', r)} data-testid={`action-activate-${r.user_id}`}>Activate</Button>
                      {r.effective_status === 'suspended'
                        ? <Button size="sm" variant="outline" onClick={() => openAction('reactivate', r)} data-testid={`action-reactivate-${r.user_id}`}><RotateCcw className="h-3 w-3 mr-1" />Reactivate</Button>
                        : <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openAction('suspend', r)} data-testid={`action-suspend-${r.user_id}`}><Ban className="h-3 w-3 mr-1" />Suspend</Button>
                      }
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {action === 'extend'     && <ExtendTrialDialog     target={target} onClose={closeAction} />}
      {action === 'activate'   && <ActivateDialog        target={target} onClose={closeAction} />}
      {action === 'suspend'    && <SuspendDialog         target={target} onClose={closeAction} />}
      {action === 'reactivate' && <ReactivateDialog      target={target} onClose={closeAction} />}
    </Card>
  );
}


// ---------- Modal: Extend trial ----------
function ExtendTrialDialog({ target, onClose }) {
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (days < 1 || days > 365) return toast.error('Days must be 1–365');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/subscribers/${target.user_id}/extend-trial`, { days }, { withCredentials: true });
      toast.success(`Trial extended by ${days} days for ${target.email}`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to extend trial');
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="extend-trial-dialog">
        <DialogHeader>
          <DialogTitle>Extend trial · {target.name}</DialogTitle>
          <DialogDescription>{target.email} — current trial ends {formatDate(target.trial_ends_at)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Extend by (days)</Label>
          <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(parseInt(e.target.value || '0', 10))} data-testid="extend-days-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="extend-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="extend-submit">{submitting ? 'Saving…' : 'Extend trial'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Modal: Manual activate (full form) ----------
function ActivateDialog({ target, onClose }) {
  const [plan, setPlan] = useState('quarterly');
  const [months, setMonths] = useState(3);
  const [amount, setAmount] = useState(5000);
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState('upi');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // When plan changes, prefill months + amount defaults from the plan catalog.
  const planDefaults = { quarterly: { months: 3, amount: 5000 }, half_yearly: { months: 6, amount: 7500 }, yearly: { months: 12, amount: 9999 } };
  useEffect(() => { const d = planDefaults[plan]; if (d) { setMonths(d.months); setAmount(d.amount); } /* eslint-disable-next-line */ }, [plan]);

  const submit = async () => {
    if (!reference.trim()) return toast.error('Payment reference is required');
    if (amount < 0) return toast.error('Amount cannot be negative');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/subscribers/${target.user_id}/activate`, {
        plan, months, amount_inr: amount, payment_reference: reference, payment_method: method, note: note || null,
      }, { withCredentials: true });
      toast.success(`Activated ${plan} for ${target.email}`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to activate');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="activate-dialog">
        <DialogHeader>
          <DialogTitle>Manually activate · {target.name}</DialogTitle>
          <DialogDescription>{target.email} — for offline payments received directly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Plan</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="activate-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">3 Months · ₹5,000</SelectItem>
                  <SelectItem value="half_yearly">6 Months · ₹7,500</SelectItem>
                  <SelectItem value="yearly">12 Months · ₹9,999</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration (months)</Label>
              <Input type="number" min={1} max={60} value={months} onChange={(e) => setMonths(parseInt(e.target.value || '0', 10))} data-testid="activate-months" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount received (₹)</Label>
              <Input type="number" min={0} value={amount} onChange={(e) => setAmount(parseInt(e.target.value || '0', 10))} data-testid="activate-amount" />
            </div>
            <div>
              <Label>Payment method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="activate-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Payment reference / transaction ID *</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. UPI txn ID, bank ref, cheque #" data-testid="activate-reference" />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything future-you needs to remember" data-testid="activate-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="activate-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="activate-submit">{submitting ? 'Activating…' : 'Activate subscription'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Modal: Suspend ----------
function SuspendDialog({ target, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/subscribers/${target.user_id}/suspend`, {}, { withCredentials: true });
      toast.success(`${target.email} suspended`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to suspend');
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="suspend-dialog">
        <DialogHeader>
          <DialogTitle>Suspend {target.name}?</DialogTitle>
          <DialogDescription>They&rsquo;ll keep read access to their own data but can&rsquo;t create or edit anything until reactivated. This is reversible.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="suspend-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="suspend-submit">{submitting ? 'Suspending…' : 'Suspend account'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Modal: Reactivate ----------
function ReactivateDialog({ target, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/subscribers/${target.user_id}/reactivate`, {}, { withCredentials: true });
      toast.success(`${target.email} reactivated`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="reactivate-dialog">
        <DialogHeader>
          <DialogTitle>Reactivate {target.name}?</DialogTitle>
          <DialogDescription>Restores their previous status (trial or active). If nothing was set before, they&rsquo;ll land on the standard paywall.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="reactivate-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="reactivate-submit">{submitting ? 'Reactivating…' : 'Reactivate'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Audit log — read-only feed of admin actions
// ============================================================
function AuditLogPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/audit-log`, { params: { limit: 200 }, withCredentials: true });
        setEntries(data.entries || []);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load audit log'); }
      finally { setLoading(false); }
    })();
  }, []);

  const actionLabel = {
    extend_trial: 'Extended trial',
    activate_offline: 'Activated (offline)',
    suspend: 'Suspended',
    reactivate: 'Reactivated',
  };

  return (
    <Card className="p-4">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">Recent admin actions</h2>
      {loading && <div className="text-center py-6 text-slate-500" data-testid="audit-loading">Loading…</div>}
      {!loading && entries.length === 0 && <div className="text-center py-6 text-slate-500" data-testid="audit-empty">No admin actions recorded yet.</div>}
      {!loading && entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Admin</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">Target</th>
                <th className="px-3 py-2 text-left">Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.audit_id} className="border-b border-slate-100" data-testid={`audit-row-${e.audit_id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(e.timestamp)}</td>
                  <td className="px-3 py-2">{e.admin_email}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{actionLabel[e.action] || e.action}</Badge></td>
                  <td className="px-3 py-2">{e.target_email}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-md truncate" title={JSON.stringify(e.details)}>{JSON.stringify(e.details)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ============================================================
// Panel 4a — Activity (per-subscriber last-login + counts)
// ============================================================
function ActivityPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/subscribers`, { params: { sort_by: 'last_login', order: 'desc' }, withCredentials: true });
        const base = (data.subscribers || []).filter((r) => r.role !== 'admin');
        setRows(base.map((r) => ({ ...r, _activity: null })));
        setLoading(false);
        // Fetch per-user counts in parallel (limit to 20 to keep the panel snappy on cold load).
        const slice = base.slice(0, 20);
        const results = await Promise.all(slice.map((r) =>
          axios.get(`${API}/api/admin/subscribers/${r.user_id}/activity`, { withCredentials: true })
            .then((res) => ({ user_id: r.user_id, data: res.data }))
            .catch(() => ({ user_id: r.user_id, data: null }))
        ));
        setRows((prev) => {
          const map = Object.fromEntries(results.map((x) => [x.user_id, x.data]));
          return prev.map((r) => ({ ...r, _activity: map[r.user_id] ?? r._activity }));
        });
        setEnriching(false);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load activity');
        setLoading(false); setEnriching(false);
      }
    })();
  }, []);

  const quietThresholdDays = 7;
  const isQuiet = (lastLogin) => {
    if (!lastLogin) return true;
    const diff = (Date.now() - new Date(lastLogin).getTime()) / (1000 * 60 * 60 * 24);
    return diff >= quietThresholdDays;
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900">Subscriber activity</h2>
        <span className="text-xs text-slate-500">Quiet = no login in last {quietThresholdDays} days · showing first 20 by last-login</span>
      </div>
      {loading ? (
        <div className="text-center py-6 text-slate-500" data-testid="activity-loading">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="activity-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left">Agent</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Last login</th>
                <th className="px-3 py-2 text-right">Clients</th>
                <th className="px-3 py-2 text-right">Policies</th>
                <th className="px-3 py-2 text-right">Leads</th>
                <th className="px-3 py-2 text-right">Tasks</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500" data-testid="activity-empty">No non-admin subscribers yet.</td></tr>}
              {rows.slice(0, 20).map((r) => (
                <tr key={r.user_id} className={`border-b border-slate-100 ${isQuiet(r.last_login) ? 'bg-amber-50/40' : ''}`} data-testid={`activity-row-${r.user_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={r.effective_status} /></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.last_login ? formatDateTime(r.last_login) : <span className="text-red-600 font-medium">Never</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{r._activity ? r._activity.clients_count : (enriching ? '…' : '—')}</td>
                  <td className="px-3 py-2 text-right">{r._activity ? r._activity.policies_count : (enriching ? '…' : '—')}</td>
                  <td className="px-3 py-2 text-right">{r._activity ? r._activity.leads_count : (enriching ? '…' : '—')}</td>
                  <td className="px-3 py-2 text-right">{r._activity ? r._activity.tasks_count : (enriching ? '…' : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ============================================================
// Panel 4b — Revenue & aggregate breakdown
// ============================================================
function RevenuePanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/stats`, { withCredentials: true });
        setStats(data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load stats'); }
      finally { setLoading(false); }
    })();
  }, []);

  const cards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Total subscribers', value: stats.total_subscribers, hint: 'All roles', testId: 'stat-total' },
      { label: 'On trial',       value: stats.status_breakdown.trial     || 0, hint: 'Trial window active',      testId: 'stat-trial' },
      { label: 'Paid & active',  value: stats.status_breakdown.active    || 0, hint: 'Verified paid subscription', testId: 'stat-active' },
      { label: 'Expired',        value: stats.status_breakdown.expired   || 0, hint: 'Was paid, now lapsed',     testId: 'stat-expired' },
      { label: 'Unpaid',         value: stats.status_breakdown.unpaid    || 0, hint: 'Trial ended, never paid',  testId: 'stat-unpaid' },
      { label: 'Suspended',      value: stats.status_breakdown.suspended || 0, hint: 'Manually frozen',          testId: 'stat-suspended' },
    ];
  }, [stats]);

  if (loading) return <Card className="p-6 text-center text-slate-500" data-testid="revenue-loading">Loading…</Card>;
  if (!stats)  return <Card className="p-6 text-center text-slate-500">No stats available.</Card>;

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white" data-testid="revenue-hero">
        <p className="text-sm opacity-90">Total revenue collected</p>
        <p className="text-4xl font-bold mt-1" data-testid="revenue-total">{formatINR(stats.revenue_inr)}</p>
        <p className="text-xs opacity-80 mt-2">
          Across {stats.total_payments} payment{stats.total_payments === 1 ? '' : 's'} ·
          {' '}Razorpay {formatINR(stats.revenue_by_source.razorpay?.amount)} ·
          {' '}Offline {formatINR(stats.revenue_by_source.offline_admin?.amount)}
        </p>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {cards.map((c) => (
          <Card key={c.label} className="p-4" data-testid={c.testId}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{c.label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{c.value}</p>
            <p className="text-xs text-slate-500 mt-1">{c.hint}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
